import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';

export type DropdownItem = {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** `danger` pour les actions destructrices (déconnexion, suppression). */
  tone?: 'default' | 'danger';
};

export type DropdownMenuProps = {
  /** Contenu du bouton qui ouvre le menu. */
  trigger: ReactNode;
  items: readonly DropdownItem[];
  /** En-tête non cliquable du panneau (nom, email…). */
  header?: ReactNode;
  align?: 'start' | 'end';
  triggerClassName?: string;
  className?: string;
  'aria-label'?: string;
};

/** Position du panneau, en coordonnées de viewport (`position: fixed`). */
type Position = {
  top: number;
  /** Ancré par un bord ou l'autre selon `align` : la largeur reste libre. */
  left?: number;
  right?: number;
};

/** Marge conservée avec le bord de la fenêtre quand le panneau bascule. */
const MARGE = 8;

/**
 * Menu déroulant : bouton + panneau, fermeture au clic extérieur et à Échap.
 *
 * Le panneau est rendu dans un portail sur `document.body`, en `position:
 * fixed`. C'est indispensable : dans une cellule de `DataTable`, un panneau
 * positionné normalement serait rogné par l'`overflow` du conteneur qui gère
 * le défilement horizontal. Le portail l'en sort, et les coordonnées sont
 * recalculées à chaque défilement ou redimensionnement pour qu'il reste collé
 * à son bouton.
 */
export function DropdownMenu({
  trigger,
  items,
  header,
  align = 'end',
  triggerClassName,
  className,
  ...props
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const placer = useCallback(() => {
    const bouton = triggerRef.current;
    if (!bouton) return;

    const rect = bouton.getBoundingClientRect();
    const hauteurPanneau = panelRef.current?.offsetHeight ?? 0;

    // Bascule au-dessus du bouton quand le bas de la fenêtre est trop proche.
    const placeEnDessous = window.innerHeight - rect.bottom;
    const versLeHaut = hauteurPanneau > 0 && placeEnDessous < hauteurPanneau + MARGE;

    setPosition({
      top: versLeHaut ? rect.top - hauteurPanneau - MARGE : rect.bottom + MARGE,
      // On ancre par un bord plutôt que de calculer `left` à partir d'une
      // largeur codée en dur : le panneau peut grandir sans décaler.
      ...(align === 'end'
        ? { right: Math.max(MARGE, window.innerWidth - rect.right) }
        : { left: Math.max(MARGE, rect.left) }),
    });
  }, [align]);

  // Première mesure avant peinture : le panneau ne s'affiche jamais au mauvais
  // endroit, même une frame.
  useLayoutEffect(() => {
    if (open) placer();
  }, [open, placer]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const cible = event.target as Node;
      if (triggerRef.current?.contains(cible) || panelRef.current?.contains(cible)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    // `capture` : attrape aussi le défilement des conteneurs intermédiaires
    // (le `overflow-x-auto` du tableau), qui ne remonte pas jusqu'à `window`.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', placer, true);
    window.addEventListener('resize', placer);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', placer, true);
      window.removeEventListener('resize', placer);
    };
  }, [open, placer]);

  const panneau = (
    <div
      ref={panelRef}
      id={menuId}
      role="menu"
      // Un portail React laisse les événements remonter l'arbre **React**, pas
      // le DOM : sans cela, cliquer une action déclencherait aussi le
      // `onRowClick` de la ligne qui contient le menu.
      onClick={(event) => event.stopPropagation()}
      style={{ position: 'fixed', ...position }}
      className={cn(
        'z-50 min-w-56 overflow-hidden rounded-card border border-border-subtle bg-surface py-1 shadow-card',
        // Tant que la position n'est pas mesurée, le panneau reste invisible
        // plutôt que d'apparaître dans le coin supérieur gauche.
        position === null && 'invisible',
        className,
      )}
    >
      {header ? <div className="border-b border-border-subtle px-4 py-3">{header}</div> : null}

      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          onClick={() => {
            setOpen(false);
            item.onSelect();
          }}
          className={cn(
            'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
            item.tone === 'danger'
              ? 'text-danger hover:bg-danger-soft'
              : 'text-content hover:bg-surface-muted',
          )}
        >
          {item.icon ? <span className="flex shrink-0 [&>svg]:size-5">{item.icon}</span> : null}
          {item.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(event) => {
          // Une ligne de tableau cliquable ne doit pas naviguer quand on
          // ouvre son menu d'actions.
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        className={cn(
          'flex items-center gap-2.5 rounded-lg p-1 transition-colors hover:bg-surface-muted',
          open && 'bg-surface-muted',
          triggerClassName,
        )}
        {...props}
      >
        {trigger}
      </button>

      {open ? createPortal(panneau, document.body) : null}
    </>
  );
}
